import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { TicketService } from "../services/ticketService";
import { RootState } from "./store";
import { ITicket } from "../interfaces/ITicket";

// Selectors
export const selectTicketsLoading = (state: RootState) => state.tickets.loading;
export const selectTickets = (state: RootState) => state.tickets.tickets;

// Async actions
export const fetchTicketsByEvent = createAsyncThunk<
    ITicket[],
    { eventId: string; apiClient: AxiosInstance },
    { state: RootState }
>(
    "tickets/fetchByEvent",
    async ({ eventId, apiClient }) => {
        const ticketService = new TicketService(apiClient);
        return await ticketService.getAll(eventId);
    }
);

export const addTicket = createAsyncThunk<
    ITicket,
    { ticket: ITicket; eventId: string; apiClient: AxiosInstance }
>(
    "tickets/add",
    async ({ ticket, eventId, apiClient }) => {
        const ticketService = new TicketService(apiClient);
        return await ticketService.create(eventId, ticket);
    }
);

export const updateTicket = createAsyncThunk<
    ITicket,
    { ticketId: string; updatedTicket: ITicket; eventId: string; apiClient: AxiosInstance }
>(
    "tickets/update",
    async ({ ticketId, updatedTicket, eventId, apiClient }) => {
        const ticketService = new TicketService(apiClient);
        return await ticketService.update(eventId, ticketId, updatedTicket);
    }
);

export const deleteTicket = createAsyncThunk<
    string,
    { ticketId: string; eventId: string; apiClient: AxiosInstance }
>(
    "tickets/delete",
    async ({ ticketId, eventId, apiClient }) => {
        const ticketService = new TicketService(apiClient);
        await ticketService.delete(eventId, ticketId);
        return ticketId;
    }
);

const ticketsSlice = createSlice({
    name: "tickets",
    initialState: {
        tickets: [] as ITicket[],
        loading: false,
        lastFetched: 0,
        lastEventId: null as string | null,
    },
    reducers: {
        resetTickets: (state) => {
            state.tickets = [];
            state.loading = false;
            state.lastFetched = 0;
            state.lastEventId = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTicketsByEvent.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTicketsByEvent.fulfilled, (state, action) => {
                state.loading = false;
                state.tickets = action.payload;
                state.lastFetched = Date.now();
                state.lastEventId = action.meta.arg.eventId;
            })
            .addCase(addTicket.fulfilled, (state, action) => {
                state.tickets.push(action.payload);
            })
            .addCase(updateTicket.fulfilled, (state, action) => {
                state.tickets = state.tickets.map((t) =>
                    t.id === action.payload.id ? action.payload : t
                );
            })
            .addCase(deleteTicket.fulfilled, (state, action) => {
                state.tickets = state.tickets.filter((t) => t.id !== action.payload);
            });
    },
});

export const { resetTickets } = ticketsSlice.actions;
export default ticketsSlice.reducer;
